import * as React from 'react';
import { graphql } from 'react-apollo';

import { branch, compose, withHandlers, withProps, withState } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import User from 'js/lib/user';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import type { CourseInfo } from 'bundles/campus-basic-contract-renewal/components/learner/withFreemiumCourseReenrollData';
import withFreemiumCourseReenrollData from 'bundles/campus-basic-contract-renewal/components/learner/withFreemiumCourseReenrollData';
import { enterpriseContractType } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import type { EnterpriseContractTagsV1 as EnterpriseContractTagsV1Type } from 'bundles/naptimejs/resources/__generated__/EnterpriseContractTagsV1';
import type { EnterpriseContractsV1 as EnterpriseContractsV1Type } from 'bundles/naptimejs/resources/__generated__/EnterpriseContractsV1';
import type { EnterpriseProgramsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseProgramsV1';
import type { ProgramBrowsingExperiencesV1 as ProgramBrowsingExperiencesV1Type } from 'bundles/naptimejs/resources/__generated__/ProgramBrowsingExperiencesV1';
import type { ThirdPartyOrganizationsV1 as ThirdPartyOrganizationsV1Type } from 'bundles/naptimejs/resources/__generated__/ThirdPartyOrganizationsV1';
import EnterpriseNoticeAcceptanceLogsV1 from 'bundles/naptimejs/resources/enterpriseNoticeAcceptanceLogs.v1';
import ExternallyAccessibleNostosV1 from 'bundles/naptimejs/resources/externallyAccessibleNostos.v1';
import ProgramBrowsingExperienceV1 from 'bundles/naptimejs/resources/programBrowsingExperiences.v1';
import ProgramMembershipsV2 from 'bundles/naptimejs/resources/programMemberships.v2';
import TargetSkillProfileUserStatesV1 from 'bundles/naptimejs/resources/targetSkillProfileUserStates.v1';
import ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import { PROGRAM_VISIBILITY_RULE } from 'bundles/program-common/constants/ProgramCommonConstants';
import filterExistsOrDefault from 'bundles/program-common/utils/filterExistsOrDefault';
import type {
  TargetSkillProfileUserStatesQuery as TargetSkillProfileUserStatesQueryData,
  TargetSkillProfileUserStatesQueryVariables,
  TargetSkillProfileUserStatesQuery_TargetSkillProfileUserStatesV1Resource_byUserAndProgram_elements as UserSkillProfileStateType,
} from 'bundles/program-home/components/__generated__/TargetSkillProfileUserStatesQuery';
import type { org_coursera_enterprise_skills_TargetSkillProfileUserGoalState as SkillProfileStates } from 'bundles/program-home/components/__generated__/globalTypes';
import {
  C4C_CORONA_TRIAL,
  C4C_FREEMIUM,
  C4C_UPSWELL,
  C4WR_CORONA_TRIAL,
  COURSERA_CORE,
  GWG_FREEMIUM,
} from 'bundles/program-home/constants/ProgramTypes';
import type {
  EnterpriseProgramBySlugQueryResult,
  EnterpriseProgramBySlugQueryVariables,
} from 'bundles/program-home/queries/EnterpriseProgramBySlugQuery';
import EnterpriseProgramBySlugQuery from 'bundles/program-home/queries/EnterpriseProgramBySlugQuery';
import FindUserEmailsByUserIdQuery from 'bundles/program-home/queries/FindUserEmailsByUserIdQuery';
import type {
  FindUserEmailsByUserIdQueryResult,
  FindUserEmailsByUserIdQueryVariables,
} from 'bundles/program-home/queries/FindUserEmailsByUserIdQuery';
import ProgramHomeLoggedInQueryText from 'bundles/program-home/queries/ProgramHomeLoggedInQuery';
import type {
  ProgramHomeLoggedInQuery,
  ProgramHomeLoggedInQueryVariables,
} from 'bundles/program-home/queries/ProgramHomeLoggedInQuery';
import { TargetSkillProfileUserStatesQuery } from 'bundles/program-home/utils/ProgramHomeGraphqlQueries';
import EnterpriseProgramContext from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import ThirdPartyOrganizationContext from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import type { PropsFromWithEnterpriseAdminRoles } from 'bundles/unified-common/hocs/withEnterpriseAdminRoles';
import withEnterpriseAdminRoles from 'bundles/unified-common/hocs/withEnterpriseAdminRoles';
import type { PropsFromWithEnterpriseProgramMembership } from 'bundles/unified-common/hocs/withEnterpriseProgramMembership';
import withEnterpriseProgramMembership from 'bundles/unified-common/hocs/withEnterpriseProgramMembership';

const WILDCARD_BROWSING_EXPERIENCE = 'DEEP_BROWSE';

type PropsFromWithProps = {
  userId: string;
  isAuthenticatedUser: boolean;
  isSuperuser: boolean;
};

type PropsFromWithEnterpriseProgramDataQuery = {
  programs?: EnterpriseProgramsV1[] | null;
  thirdPartyOrg?: ThirdPartyOrganizationsV1Type | null;
  programBrowsingExperience?: ProgramBrowsingExperiencesV1Type | null;
  programContractTags?: EnterpriseContractTagsV1Type[] | null;
  enterpriseContracts?: EnterpriseContractsV1Type[] | null;
  renderErrorPage: boolean;
};

type PropsFromCAWithProps = {
  isCareerAcademyV2: boolean;
};

type PropsFromWithEnterpriseProgramDataWithProps = {
  program: EnterpriseProgramsV1;
  programId: string;
  programName: string;
  thirdPartyOrganizationId: string;
  requirePreJoinCheck: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  orgName: string;
  isC4cv: boolean;
  isUpswell: boolean;
  isC4er: boolean;
  isC4wr: boolean;
  isC4C: boolean;
  isGwGProgram: boolean;
  isWESContract: boolean;
  showNoCertificateNotification: boolean;
  isC4Program: boolean;
};

type PropsFromWithEnterpriseProgramData = PropsFromWithEnterpriseProgramDataQuery &
  PropsFromWithEnterpriseProgramDataWithProps &
  PropsFromCAWithProps;

type PropsForProgramHomeLoggedInQuery = Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'programId' | 'thirdPartyOrganizationId' | 'requirePreJoinCheck'> &
  Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'requirePreJoinCheck'>;

type PropsFromProgramHomeLoggedInQuery = {
  renderErrorPage: boolean;
  programMembership?: ProgramMembershipsV2;
  acceptanceNoticeLogs?: EnterpriseNoticeAcceptanceLogsV1[];
  reloadProgramMembership?: () => void;
  isProgramMember: boolean;
  isInvited: boolean;
};

type PropsForDeferredProgramHomeLoggedInQuery = Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'programId'>;

type PropsFromDeferredProgramHomeLoggedInQuery = {
  renderErrorPage: boolean;
  isProgramAdmin: boolean;
};

type PropsForWithSkillSetSaving = {
  userId: number;
  program: { id: string } | undefined;
};

export type PropsFromWithSkillSetSavingWithState = {
  shouldRefetchUserSkillProfileStates: boolean;
  setShouldRefetchUserSkillProfileStates: (shouldRefetchUserSkillProfileStates: boolean) => void;
};

type PropsFromWithUserSkillProfileStatesQuery = {
  userSkillProfileStates?: UserSkillProfileStateType[];
  refetchUserSkillProfileStates?: () => Promise<unknown> | unknown;
  userSkillProfileStatesLoading?: boolean;
};

type PropsFromSaveSkillSetToggle = {
  onSkillSetSaveToggle: ({
    targetSkillProfileId,
    state,
    setSaveInProgress,
    shouldRefetch,
  }: {
    targetSkillProfileId: string;
    state: SkillProfileStates;
    setSaveInProgress?: (b: boolean) => void;
    shouldRefetch?: boolean;
  }) => Promise<void>;
};

export type PropsFromWithSkillSetSaving = PropsFromWithUserSkillProfileStatesQuery &
  PropsFromWithSkillSetSavingWithState &
  PropsFromSaveSkillSetToggle;

type PropsForWithLoggedInData = PropsForProgramHomeLoggedInQuery &
  PropsForDeferredProgramHomeLoggedInQuery &
  PropsForWithSkillSetSaving;

type PropsFromWithLoggedInData = Required<Omit<PropsFromProgramHomeLoggedInQuery, 'renderErrorPage'>> &
  Required<Omit<PropsFromDeferredProgramHomeLoggedInQuery, 'renderErrorPage'>> &
  PropsFromWithSkillSetSaving;

type PropsFromBranchAllowlistedC4ERProgram = {
  allowlistedProgramForC4er?: ExternallyAccessibleNostosV1[];
  naptime: InjectedNaptime;
};

type PropsForWithPropsAllowList = Pick<PropsFromWithEnterpriseProgramData, 'isC4cv'> &
  PropsFromBranchAllowlistedC4ERProgram;

type PropsFromWithPropsAllowList = {
  isAllowlistedC4er: boolean;
};

type PropsFromCaller = {
  children: React.ReactNode;
};

type PropsFromRouter = {
  programSlug: string;
};

type PropsFromProgram = {
  program?: EnterpriseProgramsV1;
  thirdPartyOrganization?: ThirdPartyOrganizationsV1Type;
  isProgramLoading?: boolean;
  isC4cv?: boolean;
  isUpswell?: boolean;
  isC4C?: boolean;
  hasFreemiumTag?: boolean;
  isGwGProgram?: boolean;
  showNoCertificateNotification?: boolean;
  isWildcard?: boolean;
  requirePreJoinCheck?: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
};

type PropsFromFindUserEmailsByUserIdQuery = {
  userEmails?: UserEmail[];
  refetchUserEmails?: () => Promise<unknown> | unknown;
};

type PropsFromFreemiumCourses = {
  showFreemiumCourseReenrollModal?: boolean;
  freemiumEnrollmentFilteredCourses?: CourseInfo[];
};

type PropsToComponent = PropsFromCaller &
  PropsFromProgram &
  PropsFromWithEnterpriseProgramMembership &
  PropsFromWithEnterpriseAdminRoles &
  PropsFromFindUserEmailsByUserIdQuery &
  PropsFromWithPropsAllowList &
  PropsFromWithLoggedInData &
  PropsFromFreemiumCourses & {
    programBrowsingExperience?: ProgramBrowsingExperiencesV1Type;
    programContractTags?: EnterpriseContractTagsV1Type[];
    enterpriseContracts?: EnterpriseContractsV1Type[];
    isC4er: boolean;
    isC4wr: boolean;
    isWESContract: boolean;
    isC4Program: boolean;
  };

type PropsForWithLoggedInDataBranch = {
  userId?: string;
  program?: EnterpriseProgramsV1;
  isProgramLoading?: boolean;
};

type PropsForWithSkillSetSavingWithHandlers = PropsForWithSkillSetSaving &
  PropsFromWithUserSkillProfileStatesQuery &
  PropsFromWithSkillSetSavingWithState & {
    naptime: InjectedNaptime;
  };

const withSkillSetSaving = compose<PropsFromWithSkillSetSaving, PropsForWithSkillSetSaving>(
  // Needed for naptime.executeMutation in withHandlers
  Naptime.createContainer(() => ({})),
  graphql<
    PropsForWithSkillSetSaving,
    TargetSkillProfileUserStatesQueryData,
    TargetSkillProfileUserStatesQueryVariables,
    PropsFromWithUserSkillProfileStatesQuery
  >(TargetSkillProfileUserStatesQuery, {
    // @ts-expect-error react-apollo@2.2.0
    skip: ({ userId, program }) => !userId || !program?.id || !program?.metadata?.linkedTargetSkillProfileIds?.length,
    options: ({ userId, program }) => ({
      variables: {
        userId: userId.toString(),
        // See above skip().
        programId: program!.id,
      },
    }),
    props: ({ data }) => {
      const userSkillProfileStates = filterExistsOrDefault(
        data?.TargetSkillProfileUserStatesV1Resource?.byUserAndProgram?.elements
      );
      const refetchUserSkillProfileStates = data?.refetch;
      const userSkillProfileStatesLoading = data?.loading ?? true;
      return { userSkillProfileStates, refetchUserSkillProfileStates, userSkillProfileStatesLoading };
    },
  }),
  withState('shouldRefetchUserSkillProfileStates', 'setShouldRefetchUserSkillProfileStates', false),
  withHandlers<PropsForWithSkillSetSavingWithHandlers, PropsFromSaveSkillSetToggle>({
    onSkillSetSaveToggle:
      ({ naptime, userId, refetchUserSkillProfileStates, setShouldRefetchUserSkillProfileStates }) =>
      async ({ targetSkillProfileId, state, setSaveInProgress, shouldRefetch = true }) => {
        try {
          if (!targetSkillProfileId) {
            const error = new Error('Invalid TSP id');
            Object.assign(error, { targetSkillProfileId });
            throw error;
          }
          setSaveInProgress?.(true);
          await naptime.executeMutation(
            TargetSkillProfileUserStatesV1.action('set', { userId, state, targetSkillProfileId })
          );
          if (shouldRefetch && refetchUserSkillProfileStates) {
            await refetchUserSkillProfileStates();
          } else {
            setShouldRefetchUserSkillProfileStates(true);
          }
        } finally {
          setSaveInProgress?.(false);
        }
      },
  })
);

const EnterpriseDataProvider = ({
  children,
  program,
  isProgramLoading,
  thirdPartyOrganization,
  isC4cv,
  isC4C,
  isUpswell,
  programMembership,
  isProgramMember,
  isMembershipLoading,
  isProgramAdmin,
  isInvited,
  userEmails,
  refetchUserEmails,
  hasFreemiumTag,
  isGwGProgram,
  showNoCertificateNotification,
  isWildcard,
  isAllowlistedC4er,
  userSkillProfileStates,
  refetchUserSkillProfileStates,
  userSkillProfileStatesLoading,
  shouldRefetchUserSkillProfileStates,
  setShouldRefetchUserSkillProfileStates,
  onSkillSetSaveToggle,
  showFreemiumCourseReenrollModal,
  freemiumEnrollmentFilteredCourses,
  acceptanceNoticeLogs,
  requirePreJoinCheck,
  browsingExperienceType,
  programBrowsingExperience,
  programContractTags,
  enterpriseContracts,
  isC4er,
  isC4wr,
  isWESContract,
  isC4Program,
}: PropsToComponent) => {
  return (
    <EnterpriseProgramContext.Provider
      value={{
        program,
        isProgramLoading,
        isC4cv,
        isC4C,
        isUpswell,
        programMembership,
        isProgramMember,
        isProgramAdmin,
        isInvited,
        userEmails,
        hasFreemiumTag,
        isGwGProgram,
        showNoCertificateNotification,
        isMembershipLoading,
        isWildcard,
        isAllowlistedC4er,
        userSkillProfileStates,
        refetchUserEmails,
        refetchUserSkillProfileStates,
        userSkillProfileStatesLoading,
        shouldRefetchUserSkillProfileStates,
        setShouldRefetchUserSkillProfileStates,
        onSkillSetSaveToggle,
        showFreemiumCourseReenrollModal,
        freemiumEnrollmentFilteredCourses,
        acceptanceNoticeLogs,
        requirePreJoinCheck,
        browsingExperienceType,
        programBrowsingExperience,
        programContractTags,
        enterpriseContracts,
        isC4er,
        isC4wr,
        isWESContract,
        isC4Program,
      }}
    >
      <ThirdPartyOrganizationContext.Provider value={thirdPartyOrganization}>
        {children}
      </ThirdPartyOrganizationContext.Provider>
    </EnterpriseProgramContext.Provider>
  );
};

type PropsForBranchAllowlistedC4ERProgram = Pick<PropsFromWithEnterpriseProgramData, 'isC4er' | 'programId'>;

const branchAllowlistedC4ERProgram = branch<PropsForBranchAllowlistedC4ERProgram>(
  (props) => Boolean(props.isC4er),
  Naptime.createContainer(({ programId }) => ({
    allowlistedProgramForC4er: ExternallyAccessibleNostosV1.finder('getAllProperties', {
      // eslint-disable-next-line camelcase
      params: { job_name: 'c4er_social_share_allowlist', keys: programId },
    }),
  }))
);

export default compose<PropsToComponent, PropsFromCaller>(
  connectToRouter((router) => {
    return {
      programSlug: router.params.programSlug,
    };
  }),
  graphql<PropsFromRouter, EnterpriseProgramBySlugQueryResult, EnterpriseProgramBySlugQueryVariables, PropsFromProgram>(
    EnterpriseProgramBySlugQuery,
    {
      options: ({ programSlug }) => ({ variables: { programSlug } }),
      props: ({ data }) => {
        const programs = data?.EnterpriseProgramsV1?.slug?.elements;
        const programContractTags = data?.EnterpriseProgramsV1?.slug?.linked?.enterpriseContractTagsV1;
        const programBrowsingExperience = data?.EnterpriseProgramsV1?.slug?.linked?.programBrowsingExperiencesV1?.[0];
        const enterpriseContracts = data?.EnterpriseProgramsV1?.slug?.linked?.enterpriseContractsV1;
        const contractType = enterpriseContracts?.[0]?.contractType;
        const isWESContract = contractType === enterpriseContractType.SELF_SERVE;
        const thirdPartyOrganization = data?.EnterpriseProgramsV1?.slug?.linked?.thirdPartyOrganizationsV1?.[0];

        let thirdPartyOrg = null;
        if (thirdPartyOrganization) {
          thirdPartyOrg = new ThirdPartyOrganizationsV1(thirdPartyOrganization);
        }
        const requirePreJoinCheck = thirdPartyOrg
          ? thirdPartyOrg.programVisibilityRule === PROGRAM_VISIBILITY_RULE.ACCEPTED_PRIVACY_NOTICE
          : false;

        const browsingExperienceType = programBrowsingExperience
          ? new ProgramBrowsingExperienceV1(programBrowsingExperience)
          : undefined;

        const hasFreemiumTag =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === C4C_FREEMIUM) ?? false;
        const isC4cv =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === C4C_CORONA_TRIAL) ||
          hasFreemiumTag;
        const isUpswell =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === C4C_UPSWELL) ?? false;
        const isC4C =
          programContractTags?.some((programContractTag) => programContractTag.contractTag.startsWith('C4C')) ?? false;
        const isC4wr =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === C4WR_CORONA_TRIAL) ??
          false;
        const isGwGProgram =
          programContractTags?.some((programContractTag) => GWG_FREEMIUM.includes(programContractTag.contractTag)) ??
          false;
        const showNoCertificateNotification = isUpswell || isC4cv || isC4wr || hasFreemiumTag;
        const isWildcard =
          data?.EnterpriseProgramsV1?.slug?.linked?.programBrowsingExperiencesV1?.[0].browsingExperience ===
          WILDCARD_BROWSING_EXPERIENCE;
        const isC4er =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === COURSERA_CORE) ?? false;
        const isC4Program = isC4cv || isC4er || isC4wr || isUpswell;

        return {
          programBrowsingExperience,
          programContractTags,
          enterpriseContracts,
          isC4er,
          isC4wr,
          isWESContract,
          isC4Program,
          requirePreJoinCheck,
          browsingExperienceType,
          program: programs ? programs[0] : undefined,
          thirdPartyOrganization,
          thirdPartyOrganizationId: thirdPartyOrganization?.id,
          isProgramLoading: data?.loading,
          isC4cv,
          isUpswell,
          isC4C,
          hasFreemiumTag,
          isGwGProgram,
          showNoCertificateNotification,
          isWildcard,
          programId: programs ? programs[0].id : undefined,
        };
      },
    }
  ),
  branchAllowlistedC4ERProgram,
  withProps<PropsFromWithPropsAllowList, PropsForWithPropsAllowList>(
    ({ allowlistedProgramForC4er }): PropsFromWithPropsAllowList => {
      const isAllowlistedC4er = !!allowlistedProgramForC4er?.[0]?.content?.isAllowlistedProgram;

      return { isAllowlistedC4er };
    }
  ),
  withProps<PropsFromWithProps, {}>(() => ({
    userId: User.get().id?.toString(),
    isAuthenticatedUser: User.isAuthenticatedUser(),
    isSuperuser: User.isSuperuser(),
  })),
  branch<PropsForWithLoggedInDataBranch>(
    (props) => Boolean(props.userId) && !props.program?.metadata.endedAt && !props.isProgramLoading,
    compose<PropsForWithLoggedInData, PropsFromWithLoggedInData>(
      graphql<
        PropsForProgramHomeLoggedInQuery,
        ProgramHomeLoggedInQuery,
        ProgramHomeLoggedInQueryVariables,
        PropsFromProgramHomeLoggedInQuery
      >(ProgramHomeLoggedInQueryText, {
        options: ({ programId, userId, thirdPartyOrganizationId, requirePreJoinCheck }) => ({
          variables: {
            programMembershipId: `${userId}~${programId}`,
            userId: userId.toString(),
            thirdPartyOrganizationId,
            requirePreJoinCheck,
          },
        }),
        props: ({ data, ownProps: { requirePreJoinCheck } }): PropsFromProgramHomeLoggedInQuery => {
          const programMembershipsV2 = data?.ProgramMembershipsV2?.get?.elements?.[0];
          const programMembership = programMembershipsV2 && new ProgramMembershipsV2(programMembershipsV2);
          const enterpriseNoticeAcceptanceLogsV1 = requirePreJoinCheck
            ? data?.EnterpriseNoticeAcceptanceLogsV1?.getByUserIdAndOrganizationId?.elements
            : undefined;
          const acceptanceNoticeLogs = enterpriseNoticeAcceptanceLogsV1?.map(
            (x) => new EnterpriseNoticeAcceptanceLogsV1(x)
          );
          const reloadProgramMembership = data?.refetch;
          const isProgramMember = Boolean(programMembership?.isMember);
          const isInvited = Boolean(programMembership?.isInvited);

          return {
            renderErrorPage: Boolean(data?.error),
            programMembership,
            acceptanceNoticeLogs,
            reloadProgramMembership,
            isProgramMember,
            isInvited,
          };
        },
      }),
      graphql<
        {},
        FindUserEmailsByUserIdQueryResult,
        FindUserEmailsByUserIdQueryVariables,
        PropsFromFindUserEmailsByUserIdQuery
      >(FindUserEmailsByUserIdQuery, {
        props: ({ data }) => {
          const userEmails = data?.FindUserEmailsByUserIdResponse?.userEmails;
          const refetchUserEmails = data?.refetch;
          return { userEmails, refetchUserEmails };
        },
        options: () => ({
          variables: {
            userId: `${User.get().id}`,
          },
          errorPolicy: 'all',
        }),
      }),
      withEnterpriseAdminRoles,
      withSkillSetSaving
    )
  ),
  withState('showFreemiumCourseReenrollModal', 'setShowFreemiumCourseReenrollModal', true),
  withFreemiumCourseReenrollData,
  withEnterpriseProgramMembership
)(EnterpriseDataProvider);
